import { useEffect, useMemo, useState } from "react";
import { supabase } from "../services/supabaseClient";
import { getEmpresaId } from "../services/sessionService";
import { useNavigate } from "react-router-dom";

export default function HistorialCambios() {
  const navigate = useNavigate();
  const empresaId = getEmpresaId();

  const [loading, setLoading] = useState(true);
  const [historial, setHistorial] = useState([]);
  const [instrumentos, setInstrumentos] = useState([]);
  const [instrumentoId, setInstrumentoId] = useState("");

  async function cargarInstrumentos() {
    const { data, error } = await supabase
      .from("instrumentos")
      .select("id, nombre, identificacion")
      .eq("empresa_id", empresaId)
      .order("nombre", { ascending: true });

    if (!error) setInstrumentos(data || []);
  }

  async function cargarHistorial() {
    setLoading(true);

    let query = supabase
      .from("historial_cambios")
      .select("*")
      .eq("empresa_id", empresaId)
      .order("created_at", { ascending: false })
      .limit(500);

    if (instrumentoId) query = query.eq("instrumento_id", instrumentoId);

    const { data, error } = await query;

    if (!error) setHistorial(data || []);
    else setHistorial([]);

    setLoading(false);
  }

  useEffect(() => {
    if (!empresaId) return;
    cargarInstrumentos();
    cargarHistorial();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!empresaId) return;
    cargarHistorial();
    // eslint-disable-next-line
  }, [instrumentoId]);

  const mapaInstrumentos = useMemo(() => {
    const m = new Map();
    instrumentos.forEach((i) => {
      m.set(String(i.id), `${i.nombre || ""} (${i.identificacion || i.id})`);
    });
    return m;
  }, [instrumentos]);

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-600">
            Instrumentos
          </p>

          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Historial de cambios
          </h1>

          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
            Consulta las modificaciones realizadas a los instrumentos y conserva
            trazabilidad sobre los datos actualizados.
          </p>
        </div>

        <button
          onClick={() => navigate("/instrumentos")}
          className="h-10 rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
        >
          Volver a Instrumentos
        </button>
      </div>

      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
          <div>
            <label
              htmlFor="filtro-instrumento"
              className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500"
            >
              Instrumento
            </label>

            <select
              id="filtro-instrumento"
              value={instrumentoId}
              onChange={(e) => setInstrumentoId(e.target.value)}
              className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
            >
              <option value="">Todos los instrumentos</option>

              {instrumentos.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.nombre} ({i.identificacion})
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={cargarHistorial}
            className="h-10 rounded-lg border border-slate-700 bg-slate-800 px-4 text-sm font-medium text-white transition hover:bg-slate-700"
          >
            Recargar
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 sm:px-5">
          <h2 className="font-semibold text-slate-900">
            Registro de modificaciones
          </h2>

          {!loading && (
            <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-500">
              {historial.length} registros
            </span>
          )}
        </div>

        {loading ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500">
            Cargando historial...
          </div>
        ) : historial.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <p className="font-medium text-slate-700">
              No hay registros todavía.
            </p>

            <p className="mt-1 text-sm text-slate-500">
              Los cambios realizados a los instrumentos aparecerán aquí.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[980px] w-full border-collapse text-left text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {[
                    "Fecha",
                    "Instrumento",
                    "Acción",
                    "Campo",
                    "Antes",
                    "Después",
                    "Usuario",
                  ].map((titulo) => (
                    <th
                      key={titulo}
                      className="whitespace-nowrap border-b border-slate-200 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600"
                    >
                      {titulo}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {historial.map((h) => (
                  <tr
                    key={h.id}
                    className="transition-colors hover:bg-slate-50"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                      {new Date(h.created_at).toLocaleString()}
                    </td>

                    <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-900">
                      {h.instrumento_id
                        ? mapaInstrumentos.get(String(h.instrumento_id)) ||
                          `ID ${h.instrumento_id}`
                        : "—"}
                    </td>

                    <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                      {h.accion || "—"}
                    </td>

                    <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                      {h.campo || "—"}
                    </td>

                    <td className="px-4 py-3 text-slate-500">
                      {h.valor_anterior || "—"}
                    </td>

                    <td className="px-4 py-3 text-slate-700">
                      {h.valor_nuevo || "—"}
                    </td>

                    <td className="whitespace-nowrap px-4 py-3 text-slate-500">
                      {h.usuario_email || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}